import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  History, BookOpen, FlaskConical, ArrowRight } from 'lucide-react';

const Rutgers: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Rutgers University";

    return (
        <div className="min-h-screen bg-white font-sans text-[#cc0033] selection:bg-[#cc0033] selection:text-white">
             {/* Small Top Bar */}
             <div className="bg-[#222] text-white py-1 px-6 text-[10px] font-bold uppercase tracking-widest flex justify-end gap-6">
                 <a href="#" className="hover:text-[#cc0033]">Search</a>
                 <a href="#" className="hover:text-[#cc0033]">Directory</a>
                 <a href="#" className="hover:text-[#cc0033]">Libraries</a>
             </div>

            {/* Nav */}
            <nav className="bg-[#cc0033] py-6 px-6 md:px-12 shadow-lg sticky top-0 z-50">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex flex-col">
                        <span className="font-serif text-3xl font-bold text-white leading-none uppercase tracking-wide">{schoolName.split(' ')[0]}</span>
                        <span className="font-sans text-[10px] font-bold text-white uppercase tracking-[0.3em] ml-1">The State University of New Jersey</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-white text-xs uppercase tracking-widest">
                        <a href="#" className="hover:text-black transition-colors">Information For...</a>
                        <a href="#" className="hover:text-black transition-colors">Academics</a>
                        <a href="#" className="hover:text-black transition-colors">Admissions</a>
                        <a href="#" className="hover:text-black transition-colors">Research</a>
                        <a href="#" className="hover:text-black transition-colors">Athletics</a>
                    </div>
                    
                     <div className="lg:hidden text-white">
                        <Menu size={28} />
                    </div>
                </div>
            </nav>

            {/* Hero - Revolutionary */}
            <header className="relative h-[600px] bg-red-800">
                <img 
                    src="https://images.unsplash.com/photo-1579294294021-d636195653db?q=80&w=2070&auto=format&fit=crop" 
                    alt="Rutgers Campus" 
                    className="absolute inset-0 w-full h-full object-cover mix-blend-multiply opacity-50"
                />

                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12 text-center md:text-left">
                     <div className="max-w-2xl mx-auto md:mx-0">
                         <h1 className="text-5xl md:text-7xl font-sans font-black text-white mb-6 uppercase italic transform -skew-x-6">
                             Be <br/> Revolutionary.
                         </h1>
                         <p className="text-xl md:text-2xl mb-10 font-medium text-white max-w-lg">
                             Join a community that has been revolutionizing the world since 1766.
                         </p>
                         <Button className="bg-white text-[#cc0033] hover:bg-black hover:text-white font-bold uppercase tracking-widest px-10 h-14 rounded-none shadow-lg transform -skew-x-6">
                             <span className="transform skew-x-6">Explore {schoolName.split(' ')[0]}</span>
                         </Button>
                     </div>
                </div>
            </header>

            {/* Stats - Red & Black */}
            <section className="bg-white py-20 px-6 md:px-12">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center text-[#333]">
                     <div>
                         <History size={48} className="text-[#cc0033] mx-auto mb-6" />
                         <h3 className="text-3xl font-black mb-2">1766</h3>
                         <p className="text-gray-500 font-bold uppercase text-xs tracking-widest">Founded (Patriotism Pre-dates US)</p>
                     </div>
                      <div>
                         <BookOpen size={48} className="text-[#cc0033] mx-auto mb-6" />
                         <h3 className="text-3xl font-black mb-2">Top 15</h3>
                         <p className="text-gray-500 font-bold uppercase text-xs tracking-widest">Public University (US News)</p>
                     </div>
                      <div>
                         <FlaskConical size={48} className="text-[#cc0033] mx-auto mb-6" />
                         <h3 className="text-3xl font-black mb-2">$750M+</h3>
                         <p className="text-gray-500 font-bold uppercase text-xs tracking-widest">Research Grants & Awards</p>
                     </div>
                </div>
            </section>

             {/* News List - Clean */}
             <section className="bg-[#f0f0f0] py-24 px-6 md:px-12">
                 <div className="max-w-[1000px] mx-auto">
                     <h2 className="text-3xl font-bold text-[#333] mb-12 border-l-8 border-[#cc0033] pl-6">Latest from {schoolName}</h2>

                     <div className="space-y-8">
                         {[
                             `${schoolName.split(' ')[0]} researchers develop new rapid COVID-19 test`,
                             "New Honors College welcomes largest, most diverse class",
                             "Scarlet Knights football team prepares for Big Ten season opener",
                             "Mason Gross School of the Arts presents Fall Showcase"
                         ].map((title, i) => (
                             <div key={i} className="bg-white p-6 shadow-sm flex flex-col md:flex-row gap-6 items-center group cursor-pointer hover:shadow-md transition-shadow">
                                 <div className="w-full md:w-48 h-32 bg-gray-200 overflow-hidden">
                                     <img src={`https://source.unsplash.com/random/400x300?sig=${i+40}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" alt="News"/>
                                 </div>
                                 <div className="flex-1">
                                     <h3 className="text-xl font-bold text-[#333] group-hover:text-[#cc0033] transition-colors mb-2">{title}</h3>
                                     <a href="#" className="inline-flex items-center gap-2 text-[#cc0033] font-bold text-xs uppercase tracking-widest hover:underline">Read Story <ArrowRight size={12}/></a>
                                 </div>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#222] text-gray-400 py-16 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 font-sans text-sm">
                     <div className="col-span-1 md:col-span-2">
                         <span className="font-serif font-bold text-2xl text-white mb-6 block uppercase tracking-wide">{schoolName}</span>
                         <p className="mb-6">
                             New Brunswick, NJ 08901 <br/>
                             848-932-INFO (4636)
                         </p>
                     </div>

                     <div className="grid grid-cols-2 gap-12 font-bold uppercase text-xs tracking-widest">
                          <ul className="space-y-3 hover:[&_a]:text-white">
                             <li><a href="#">About {schoolName.split(' ')[0]}</a></li>
                             <li><a href="#">Diversity</a></li>
                             <li><a href="#">Accessibility</a></li>
                             <li><a href="#">Privacy</a></li>
                         </ul>
                          <ul className="space-y-3 hover:[&_a]:text-white">
                             <li><a href="#">Work at {schoolName.split(' ')[0]}</a></li>
                             <li><a href="#">Contact Us</a></li>
                             <li><a href="#">Directions</a></li>
                         </ul>
                     </div>
                 </div>
                 <div className="max-w-[1200px] mx-auto mt-16 text-center text-xs text-gray-500 border-t border-gray-800 pt-8">
                     © 2026 {schoolName}
                 </div>
             </footer>
        </div>
    );
};

export default Rutgers;
